import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { ActivatedRoute } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { InteractionStatus } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tokenKey = 'authToken';
  private userInternalIdSubject = new BehaviorSubject<string | null>(null);
  private userIdSubject = new BehaviorSubject<string | null>(null);
  public userId$ = this.userIdSubject.asObservable();

  userInternalId$: Observable<string | null> =
    this.userInternalIdSubject.asObservable();

  constructor(
    private activatedRoute: ActivatedRoute,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {
    this.updateUserId();

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
      )
      .subscribe(() => {
        this.updateUserId();
      });
  }

  public updateUserId() {
    const id = this.getUserIdFromToken();
    this.userIdSubject.next(id);
  }

  setUserInternalId(data: string): void {
    this.userInternalIdSubject.next(data);
  }

  getUserInternalId(): string | null {
    return this.userInternalIdSubject.getValue();
  }

  clearUserInternalId(): void {
    this.userInternalIdSubject.next(null);
  }

  onLogin(token: string): Promise<void> {
    return new Promise((resolve) => {
      localStorage.setItem(this.tokenKey, token);
      resolve();
    });
  }

  logout() {
    this.msalService.logout();
    this.onLogout();
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  getToken(): string | null {
    const token = this.getTokenFromUrl();
    if (token) {
      return token;
    }

    return localStorage.getItem(this.tokenKey);
  }

  getTokenFromUrl(): string | null {
    let token: string | null = null;

    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['token']) {
        token = params['token'];
        // Só persiste em localStorage quando não há sessão MSAL ativa.
        // Com sessão ativa, escrever aqui sobrescreveria o accessToken gravado
        // pelo AppComponent via ACQUIRE_TOKEN_SUCCESS, corrompendo o fluxo MSAL.
        const hasMsalSession =
          this.msalService.instance.getAllAccounts().length > 0;
        if (!hasMsalSession) {
          localStorage.setItem(this.tokenKey, params['token']);
        }
      }
    });

    return token;
  }

  onLogout(): void {
    localStorage.removeItem(this.tokenKey);
  }

  clearLocalStorage(): void {
    localStorage.removeItem(this.tokenKey);
  }

  getUserIdFromToken(): string | null {
    // MSAL é a fonte autoritativa quando há sessão ativa.
    // Envolvido em try-catch pois getActiveAccount()/getAllAccounts() lançam
    // BrowserAuthError se chamados antes de initialize() completar — o que
    // ocorre no constructor do AuthService durante o bootstrap do Angular.
    // Após initialize(), sanitizeAuthToken() chama updateUserId() novamente
    // e o bloco MSAL funciona corretamente.
    try {
      let activeAccount = this.msalService.instance.getActiveAccount();

      if (!activeAccount) {
        const allAccounts = this.msalService.instance.getAllAccounts();
        if (allAccounts.length > 0) {
          activeAccount = allAccounts[0];
          this.msalService.instance.setActiveAccount(activeAccount);
        }
      }

      if (activeAccount) {
        return activeAccount.idTokenClaims?.oid || null;
      }
    } catch {
      // MSAL ainda não inicializado — fallback para localStorage abaixo.
    }

    // Sem sessão MSAL ativa ou MSAL não inicializado ainda.
    // Cobre fluxo não autenticado (pré-cadastro HS256) e bootstrap inicial.
    const token = localStorage.getItem(this.tokenKey);
    if (token) {
      try {
        const decodedToken: any = jwtDecode(token);
        return decodedToken.oid || null;
      } catch (error) {
        console.error('Invalid token in storage', error);
        return null;
      }
    }

    return null;
  }
}
